"use client"

import React from 'react'
import TextInput from '../../../shared/components/inputs/text-input'
import { Avatar, AvatarFallback, AvatarImage } from '../../../shared/components/ui/avatar'
import { Button } from '../../../shared/components/ui/button'
import { TypographyH3, TypographySmall } from '../../../shared/components/ui/typography'
import { useActiveChat } from '@/features/messages/context/active-chat-context'
import { ChevronLeft, EllipsisVertical, Paperclip, SendHorizontal } from 'lucide-react'
import { cn, shortenName } from '../../../shared/lib/utils'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../../../shared/components/ui/dropdown-menu"
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../../../shared/components/ui/dialog"
import Image from 'next/image'
import { deleteIconPng } from '../../../shared/constants/images'


const MESSAGES_LIST = [
    {
        role: "user",
        message: "Hello, how can I help you?",
    },
    {
        role: "other_user",
        message: "I need help with my account",
    }
]

/**
 * Chat Group Component, contains the chat header and chat messages and input
 */
function ChatDetails() {

    const { activeChat, showChatDetails, handleShowChatDetails } = useActiveChat()
    if (!activeChat) return null
    const [firstName, lastName] = activeChat.username.split(' ')
    const avatarFallbackText = shortenName(firstName, lastName)

    return (
        <div
            className={cn(
                'min-h-full hidden lg:grid grid-rows-[auto_1fr_auto]',
                {
                    "grid": showChatDetails,
                }
            )}
        >
            {/* Chat Header */}
            <div className='border-b py-5 px-5 flex justify-between'>
                <div className="flex items-center gap-3 w-full">
                    <Button 
                        variant='ghost' 
                        size='icon' 
                        className='lg:hidden'
                        onClick={() => handleShowChatDetails(false)}
                    >
                        <ChevronLeft className='w-5 h-5' size={40} />
                    </Button>
                    <div className='w-full flex items-center gap-2'>
                        <Avatar>
                            <AvatarFallback>{avatarFallbackText}</AvatarFallback>
                            <AvatarImage src="https://randomuser.me/api/port" />
                        </Avatar>
                        <div className="space-y-1.5">
                            <TypographyH3 className='text-lg font-semibold leading-none'>{activeChat.username}</TypographyH3>
                            <TypographySmall className='text-muted-foreground'>Volunteer</TypographySmall>
                        </div>
                    </div>
                </div>

                <Dialog>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant={"ghost"} size={"icon"} className='rounded-full'>
                                <EllipsisVertical />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className='mr-5'>
                            <DialogTrigger asChild>
                                <DropdownMenuItem className='border-transparent cursor-pointer'>Delete Chat</DropdownMenuItem>
                            </DialogTrigger>
                        </DropdownMenuContent>
                    </DropdownMenu>
                    <DialogContent className='px-10 gap-6'>
                        <Image
                            src={deleteIconPng}
                            alt="Delete Icon"
                            className='w-[80px] mx-auto'
                            priority
                        />
                        <DialogTitle className='w-5/6 text-center mx-auto font-medium leading-snug'>
                            Are you sure you want to delete this chat?
                        </DialogTitle>
                        <DialogFooter className="gap-3 md:gap-2">
                            <DialogClose asChild>
                                <Button variant='outline' className='w-full h-11 rounded-lg border-border text-muted-foreground hover:bg-muted'>
                                    Cancel
                                </Button>
                            </DialogClose>
                            <Button
                                variant={"destructive"}
                                showLoader={true}
                                className='w-full h-11 rounded-lg'
                            >
                                Delete
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

            </div>

            {/* Chat Messages */}
            <div className="p-5 overflow-y-auto">
                <div className="flex flex-col justify-end gap-5 min-h-full ">
                    <TypographySmall className='self-center text-muted-foreground/60'>Thursday, Jan 4 6:21 PM</TypographySmall>
                    {MESSAGES_LIST.map(({ role, message }) => {
                        return (
                            <div
                                key={message}
                                className={`flex items-center gap-2 ${role === "user" ? "justify-end" : ""}`}
                            >
                                <div className={`p-3 rounded-lg ${role === "user" ? "bg-primary text-white" : "bg-gray-100 text-gray-800"}`}>
                                    {message}
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>

            {/* Chat Input */}
            <div className='p-5 flex gap-2 items-center'>
                <TextInput
                    className='border rounded-full h-12 w-full shadow'
                    rightIcon={<Paperclip className='w-4' />}
                    placeholder='Write message here...'
                />
                <Button size={"icon"} variant={"ghost"} className='shadow w-11 rounded-full h-11 border'>
                    <SendHorizontal />
                </Button>
            </div>
        </div>
    )
}

export default ChatDetails